import { DownloadWindowView } from '../view/DownloadWindowView';
import { templateDownloadWindow } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig
} from '../../../framework/BaseWebComponentController';
import { DownloadWindowWebComponent } from '..';
import {
  DownloadWindowWebComponentSchema,
  START_VALIDATE_DOWNLOAD_PATH,
  VALIDATE_ARCHIVE_PASSWORD,
  VALIDATE_DOWNLOAD_PATH,
  VALIDATE_PASSWORD_TOGGLE,
  VALIDATE_REPEAT_PASSWORD,
} from '../types';
import { ToggleEvents } from '../../../ui/gzToggle/types';
import { translateFn } from '../../../utils/translator.helper';
import { PrevalidateUploadPathForEndpointErrorCodes } from '../../terminal/types';
import { convertUnits } from '../../uploadWindow/utils';

export class DownloadWindowController extends BaseWebComponentController {
  public component: DownloadWindowWebComponent;
  public view: DownloadWindowView;
  public viewConstructor: typeof DownloadWindowView;

  constructor(component: DownloadWindowWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: DownloadWindowView,
      viewTemplate: templateDownloadWindow,
    });
  }

  async getDataForView(): Promise<DownloadWindowWebComponentSchema> {
    return {
      path: this.component.path,
      streamId: this.component.streamId,
      maxFilesSize: this.component.maxFilesSize,
    };
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$passwordProtectToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onPasswordProtectClick,
      },
      {
        element: this.view.dom.$downloadPathInput,
        event: 'blur',
        callback: this.onPathBlur,
      },
      {
        element: this.view.dom.$downloadPathInput,
        event: 'change',
        callback: this.onPathChange,
      },
      {
        element: this.view.dom.$archivePasswordInput,
        event: 'change',
        callback: this.onArchivePasswordChange,
      },
      {
        element: this.view.dom.$archivePasswordInput,
        event: 'blur',
        callback: this.onArchivePasswordBlur,
      },
      {
        element: this.view.dom.$repeatPasswordInput,
        event: 'change',
        callback: this.onRepeatPasswordChange,
      },
      {
        element: this.view.dom.$repeatPasswordInput,
        event: 'blur',
        callback: this.onRepeatPasswordBlur,
      },
    ];
  }

  async onPathBlur() {
    await this.validateDownloadPath();
  }

  async onPathChange() {
    this.component.path = this.view.dom.$downloadPathInput.getFormValue();
  }

  async onArchivePasswordChange() {
    this.component.password = this.view.dom.$archivePasswordInput.getFormValue();

    this.view.setPasswordValidators();
    this.dispatchEvent(VALIDATE_ARCHIVE_PASSWORD, {});
  }

  async onArchivePasswordBlur() {
    this.dispatchEvent(VALIDATE_ARCHIVE_PASSWORD, {});
  }

  async onRepeatPasswordChange() {
    this.component.repeatPassword = this.view.dom.$repeatPasswordInput.getFormValue();

    this.view.setPasswordValidators();
    this.dispatchEvent(VALIDATE_REPEAT_PASSWORD, {});
  }

  async onRepeatPasswordBlur() {
    this.dispatchEvent(VALIDATE_REPEAT_PASSWORD, {});
  }

  onPasswordProtectClick() {
    this.component.password = '';
    this.component.repeatPassword = '';
    this.view.checkPasswordProtectForms();

    this.dispatchEvent(VALIDATE_PASSWORD_TOGGLE, {});
  }

  public async validateDownloadPath() {
    this.view.dom.$downloadPathField.markAsValid();
    this.view.dom.$downloadPathField.touched = true;
    let valid = await this.view.dom.$downloadPathField.dryValidate();

    let code;
    try {
      if (valid) {
        this.setPathReadOnly(true);
        this.dispatchEvent(START_VALIDATE_DOWNLOAD_PATH, {});

        const response = await this.dependencies.api.clients.RemoteShell.makeRequest(
          'gzConsoleRemoteShell/validateDownloadPath',
          {
            path: this.view.dom.$downloadPathInput.getFormValue(),
            streamId: this.component.streamId,
          }
        ).toPromise();

        const validResponse = response.errorCode === PrevalidateUploadPathForEndpointErrorCodes.SUCCESS;
        valid = valid && validResponse;
        code = response.errorCode;
      }
    } catch (err) {
      console.error(err);
      valid = false;
      code = -1;
    }

    if (!this.view.dom.$downloadPathField) {
      return;
    }

    if (!valid) {
      this.view.dom.$downloadPathField.markAsInvalid(
        !code ? translateFn('Required field') : this.getMessageFromCode(code)
      );
    }

    this.setPathReadOnly(false);
    this.dispatchEvent(VALIDATE_DOWNLOAD_PATH, {});
  }

  dispatchEvent(name, detail = {}) {
    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail,
      })
    );
  }

  getMessageFromCode(code) {
    switch (code) {
      case PrevalidateUploadPathForEndpointErrorCodes.PATH_NOT_FOUND:
        return translateFn('Invalid file path.');
      case PrevalidateUploadPathForEndpointErrorCodes.INVALID_NAME:
        return translateFn('No file path was specified, or the file path contains unsupported characters.');
      case PrevalidateUploadPathForEndpointErrorCodes.RELATIVE_PATH:
        return translateFn('Relative file path. Please provide the full file path.');
      case PrevalidateUploadPathForEndpointErrorCodes.EMPTY_DIRECTORY:
        return translateFn(' Empty folder. Choose a different folder.');
      case PrevalidateUploadPathForEndpointErrorCodes.INVALID_PROFILE:
        return translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
          ERROR_CODE: code,
        });
      case PrevalidateUploadPathForEndpointErrorCodes.ACCESS_DENIED:
        return translateFn('Insufficient permissions. Choose a different file or folder.');
      case PrevalidateUploadPathForEndpointErrorCodes.MAX_FILE_SIZE_EXCEEDED:
        return translateFn('The overall download size cannot exceed {MAX_SIZE}.', {
          MAX_SIZE: convertUnits(this.component.maxFilesSize),
        });
      default:
        return translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
          ERROR_CODE: code,
        });
    }
  }

  setPathReadOnly(value) {
    if (this.view.dom.$downloadPathField?.Input) {
      this.view.dom.$downloadPathField.Input.readonly = value;
    }
  }
}
